import React, { useEffect, useRef, useState } from 'react';
import './styles.scss';

type ContractState = 'open' | 'accepted' | 'delivered' | 'settled' | 'cancelled' | 'expired' | 'disputed';
type Role = 'creator' | 'worker' | 'none';
type Tab = 'available' | 'mine' | 'create';

interface ContractEvent {
  at: number;
  state: ContractState;
  by: string;
}

interface ContractView {
  id: number;
  category: string;
  categoryLabel: string;
  title: string;
  description: string;
  reward: number;
  creatorName: string;
  workerName: string | null;
  role: Role;
  state: ContractState;
  stateLabel: string;
  createdAt: number;
  deadlineAt: number;
  deliveredAt: number | null;
  reviewAt: number | null;
  events: ContractEvent[];
}

interface CategoryInfo { id: string; label: string }

interface Limits {
  minReward: number;
  maxReward: number;
  maxTitle: number;
  maxDescription: number;
  maxOpen: number;
  maxAccepted: number;
}

interface ContractsState {
  now: number;
  money: number;
  available: ContractView[];
  mine: ContractView[];
  categories: CategoryInfo[];
  deadlineDays: number[];
  limits: Limits;
}

interface Notice { ok: boolean; message: string }

// Same click-grace guard as the other overlays: browser focus flaps while the
// panel is toggled on, so a click right after opening is ignored.
const CLICK_GRACE_MS = 250;
// No answer from the server after this long: show the retry state
const LOAD_TIMEOUT_MS = 6000;

const send = (payload: Record<string, unknown>): void => {
  try {
    (window as any).skyrimPlatform.sendMessage(JSON.stringify({ panel: 'contracts', ...payload }));
  } catch (e) {
    // Running outside the game (dev preview): log instead.
    // eslint-disable-next-line no-console
    console.log('contracts sendMessage', payload);
  }
};

function formatRemaining(ms: number): string {
  if (ms <= 0) return 'maintenant';
  const minutes = Math.max(1, Math.round(ms / 60_000));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const m = minutes % 60;
    return m === 0 ? `${hours} h` : `${hours} h ${String(m).padStart(2, '0')}`;
  }
  const days = Math.floor(hours / 24);
  const h = hours % 24;
  return h === 0 ? `${days} j` : `${days} j ${h} h`;
}

function formatDate(ts: number): string {
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const formatDeadlineChoice = (days: number) => (days === 1 ? '1 jour' : `${days} jours`);

const EVENT_LABEL: Record<ContractState, string> = {
  open: 'Créé',
  accepted: 'Accepté',
  delivered: 'Livré',
  settled: 'Réglé',
  cancelled: 'Annulé',
  expired: 'Expiré',
  disputed: 'Contesté',
};

const EMPTY_FORM = { category: '', title: '', description: '', reward: '', deadlineDays: 3 };

const Contracts: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [tab, setTab] = useState<Tab>('available');
  const [data, setData] = useState<ContractsState | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [filter, setFilter] = useState('');
  const [armed, setArmed] = useState<string | null>(null); // "<action>:<id>" waiting for a second click
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [, setTick] = useState(0);

  const openedAtRef = useRef(0);
  const clockOffsetRef = useRef(0); // server time minus local time
  const creatingRef = useRef(false);

  useEffect(() => {
    const showHandler = () => {
      openedAtRef.current = Date.now();
      setTab('available');
      setData(null);
      setTimedOut(false);
      setNotice(null);
      setFilter('');
      setArmed(null);
      setSelectedId(null);
      setForm(EMPTY_FORM);
      creatingRef.current = false;
      setVisible(true);
    };
    const hideHandler = () => setVisible(false);
    const stateHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail as ContractsState | null;
      if (!detail || !Array.isArray(detail.available) || !Array.isArray(detail.mine)) return;
      clockOffsetRef.current = (detail.now || Date.now()) - Date.now();
      setData(detail);
      setTimedOut(false);
    };
    const resultHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail as Notice | null;
      if (!detail) return;
      setNotice({ ok: detail.ok === true, message: String(detail.message || '') });
      setArmed(null);
      if (creatingRef.current) {
        creatingRef.current = false;
        if (detail.ok) {
          setForm(EMPTY_FORM);
          setTab('mine');
        }
      }
    };

    window.addEventListener('skymp5-client:showContracts', showHandler);
    window.addEventListener('skymp5-client:hideContracts', hideHandler);
    window.addEventListener('skymp5-client:contractsState', stateHandler);
    window.addEventListener('skymp5-client:contractsResult', resultHandler);
    return () => {
      window.removeEventListener('skymp5-client:showContracts', showHandler);
      window.removeEventListener('skymp5-client:hideContracts', hideHandler);
      window.removeEventListener('skymp5-client:contractsState', stateHandler);
      window.removeEventListener('skymp5-client:contractsResult', resultHandler);
    };
  }, []);

  // Countdowns
  useEffect(() => {
    if (!visible) return undefined;
    const timer = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(timer);
  }, [visible]);

  // Retry state when the server never answers
  useEffect(() => {
    if (!visible || data) return undefined;
    const timer = setTimeout(() => setTimedOut(true), LOAD_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [visible, data]);

  if (!visible) return null;

  const graceOver = () => Date.now() - openedAtRef.current >= CLICK_GRACE_MS;
  const serverNow = () => Date.now() + clockOffsetRef.current;

  const close = () => {
    if (!graceOver()) return;
    setVisible(false);
    send({ action: 'close' });
  };

  const refresh = () => {
    setTimedOut(false);
    send({ action: 'request' });
  };

  const goTab = (next: Tab) => {
    if (!graceOver()) return;
    setTab(next);
    setNotice(null);
    setArmed(null);
    setSelectedId(null);
    if (next !== 'create') send({ action: 'request' });
  };

  const act = (action: string, id: number) => {
    if (!graceOver()) return;
    setNotice(null);
    setArmed(null);
    send({ action, id });
  };

  // Destructive actions need a second click on the same button
  const actConfirmed = (action: string, id: number) => {
    if (!graceOver()) return;
    const key = `${action}:${id}`;
    if (armed !== key) {
      setArmed(key);
      return;
    }
    act(action, id);
  };

  const limits = data?.limits;
  const categories = data?.categories ?? [];
  const money = data?.money ?? 0;

  const rewardNumber = Number(form.reward);
  const rewardValid =
    form.reward.trim() !== '' &&
    Number.isInteger(rewardNumber) &&
    !!limits &&
    rewardNumber >= limits.minReward &&
    rewardNumber <= limits.maxReward;
  const canAfford = rewardValid && rewardNumber <= money;
  const formValid = !!form.category && form.title.trim().length > 0 && rewardValid && canAfford;

  const submitCreate = () => {
    if (!graceOver() || !formValid) return;
    creatingRef.current = true;
    setNotice(null);
    send({
      action: 'create',
      category: form.category,
      title: form.title.trim(),
      description: form.description.trim(),
      reward: rewardNumber,
      deadlineDays: form.deadlineDays,
    });
  };

  const available = (data?.available ?? []).filter((c) => !filter || c.category === filter);
  const mine = data?.mine ?? [];
  const activeMine = mine.filter((c) => c.state === 'open' || c.state === 'accepted' || c.state === 'delivered' || c.state === 'disputed');
  const myAcceptedCount = mine.filter((c) => c.state === 'accepted' && c.role === 'worker').length;
  const myOpenCount = mine.filter((c) => c.state === 'open' && c.role === 'creator').length;

  const timing = (c: ContractView): string | null => {
    const now = serverNow();
    if (c.state === 'open' || c.state === 'accepted') {
      return c.deadlineAt <= now ? 'Échéance dépassée' : `Échéance dans ${formatRemaining(c.deadlineAt - now)}`;
    }
    if (c.state === 'delivered' && c.reviewAt !== null) {
      return `Règlement automatique dans ${formatRemaining(c.reviewAt - now)}`;
    }
    if (c.state === 'disputed') return "En attente de l'arbitrage du staff";
    return null;
  };

  const renderTag = (c: ContractView) => (
    <span className={`contracts-tag contracts-tag--${c.state}`}>{c.stateLabel}</span>
  );

  const confirmLabel = (action: string, id: number, idle: string, armedText: string) =>
    (armed === `${action}:${id}` ? armedText : idle);

  const renderMineActions = (c: ContractView): React.ReactNode => {
    if (c.role === 'creator' && c.state === 'open') {
      return (
        <button
          type="button"
          className="contracts-button contracts-button--danger"
          onClick={() => actConfirmed('cancel', c.id)}
        >
          {confirmLabel('cancel', c.id, 'Annuler le contrat', 'Confirmer l\'annulation')}
        </button>
      );
    }
    if (c.role === 'worker' && c.state === 'accepted') {
      return (
        <button type="button" className="contracts-button contracts-button--primary" onClick={() => act('deliver', c.id)}>
          Livrer
        </button>
      );
    }
    if (c.role === 'creator' && c.state === 'delivered') {
      return (
        <>
          <button
            type="button"
            className="contracts-button contracts-button--danger"
            onClick={() => actConfirmed('dispute', c.id)}
          >
            {confirmLabel('dispute', c.id, 'Contester', 'Confirmer la contestation')}
          </button>
          <button type="button" className="contracts-button contracts-button--primary" onClick={() => act('settle', c.id)}>
            Confirmer la livraison
          </button>
        </>
      );
    }
    return null;
  };

  // One row of the list: reward on the left as the thing the eye lands on,
  // title and context in the middle, state or deadline on the right.
  const renderRow = (c: ContractView, selected: boolean, onSelect: () => void) => {
    const time = timing(c);
    return (
      <li key={c.id}>
        <button
          type="button"
          className={'contracts-row' + (selected ? ' contracts-row--selected' : '')}
          onClick={() => { if (graceOver()) onSelect(); }}
          aria-pressed={selected}
        >
          <span className="contracts-row__reward">
            <strong>{c.reward}</strong>
            <span>Septims</span>
          </span>
          <span className="contracts-row__main">
            <span className="contracts-row__title">{c.title}</span>
            <span className="contracts-row__sub">
              {c.categoryLabel} par {c.role === 'creator' ? 'toi' : c.creatorName}
            </span>
          </span>
          <span className="contracts-row__side">
            {c.state !== 'open' || c.role !== 'none' ? renderTag(c) : null}
            {time && c.state !== 'disputed' && (
              <span className="contracts-row__time">
                {time.replace('Échéance dans ', '').replace('Règlement automatique dans ', '')}
              </span>
            )}
          </span>
        </button>
      </li>
    );
  };

  const renderDetail = (c: ContractView | undefined, actions: (c: ContractView) => React.ReactNode, emptyText: string) => {
    if (!c) {
      return <div className="contracts-detail contracts-detail--empty"><p>{emptyText}</p></div>;
    }
    const time = timing(c);
    const acts = actions(c);
    return (
      <div className="contracts-detail">
        <div className="contracts-detail__top">
          <div className="contracts-detail__tags">
            <span className="contracts-chip contracts-chip--static">{c.categoryLabel}</span>
            {renderTag(c)}
          </div>
          <h2 className="contracts-detail__title">{c.title}</h2>
          <div className="contracts-detail__reward">
            <strong>{c.reward}</strong> Septims
          </div>
        </div>

        <div className="contracts-detail__body">
          {c.description
            ? <p className="contracts-detail__text">{c.description}</p>
            : <p className="contracts-detail__text contracts-detail__text--none">Aucune description.</p>}

          <dl className="contracts-facts">
            <div>
              <dt>Commanditaire</dt>
              <dd>{c.role === 'creator' ? 'Toi' : c.creatorName}</dd>
            </div>
            <div>
              <dt>Travailleur</dt>
              <dd>{c.workerName ? (c.role === 'worker' ? 'Toi' : c.workerName) : 'Personne pour l\'instant'}</dd>
            </div>
            {time && (
              <div>
                <dt>Délai</dt>
                <dd>{time}</dd>
              </div>
            )}
          </dl>

          {c.role !== 'none' && c.events.length > 0 && (
            <>
              <h3 className="contracts-detail__heading">Historique</h3>
              <ul className="contracts-log">
                {c.events.map((ev, i) => (
                  <li key={`${ev.at}-${i}`}>
                    <span className="contracts-log__date">{formatDate(ev.at)}</span>
                    <span>{EVENT_LABEL[ev.state]} par {ev.by}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        {acts && <div className="contracts-detail__actions">{acts}</div>}
      </div>
    );
  };

  const renderLoading = () => (
    <div className="contracts-empty">
      {timedOut ? (
        <>
          <p>Le registre ne répond pas.</p>
          <button type="button" className="contracts-button" onClick={refresh}>Réessayer</button>
        </>
      ) : (
        <p>Chargement du registre...</p>
      )}
    </div>
  );

  const tabs: Array<{ id: Tab; label: string; count?: number }> = [
    { id: 'available', label: 'Disponibles', count: data?.available.length },
    { id: 'mine', label: 'Mes contrats', count: activeMine.length },
    { id: 'create', label: 'Créer un contrat' },
  ];

  const selectedAvailable = available.find((c) => c.id === selectedId) ?? available[0];
  const selectedMine = mine.find((c) => c.id === selectedId) ?? mine[0];

  return (
    <div className="contracts-overlay">
      <div className="contracts-panel" role="dialog" aria-label="Contrats">
        <header className="contracts-header">
          <div>
            <h1 className="contracts-header__title">Contrats</h1>
            <p className="contracts-header__sub">Missions entre joueurs, payées à la livraison.</p>
          </div>
          {data && (
            <div className="contracts-header__purse">
              <span>Bourse</span>
              <strong>{money} Septims</strong>
            </div>
          )}
        </header>

        <nav className="contracts-tabs" role="tablist" aria-label="Sections des contrats">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              className={'contracts-tab' + (tab === t.id ? ' contracts-tab--active' : '')}
              onClick={() => goTab(t.id)}
            >
              {t.label}
              {t.count !== undefined && t.count > 0 && <span className="contracts-tab__count">{t.count}</span>}
            </button>
          ))}
        </nav>

        {notice && (
          <div className={'contracts-notice ' + (notice.ok ? 'contracts-notice--ok' : 'contracts-notice--error')} role="status">
            {notice.message}
          </div>
        )}

        <div className="contracts-body">
          {!data && renderLoading()}

          {data && tab === 'available' && (
            <div className="contracts-split">
              <section className="contracts-pane">
                <div className="contracts-filter">
                  <div className="contracts-chips" role="group" aria-label="Filtrer par catégorie">
                    <button
                      type="button"
                      className={'contracts-chip' + (filter === '' ? ' contracts-chip--active' : '')}
                      onClick={() => setFilter('')}
                    >
                      Toutes
                    </button>
                    {categories.map((cat) => (
                      <button
                        key={cat.id}
                        type="button"
                        className={'contracts-chip' + (filter === cat.id ? ' contracts-chip--active' : '')}
                        onClick={() => setFilter(cat.id)}
                      >
                        {cat.label}
                      </button>
                    ))}
                  </div>
                  <div className="contracts-hint">
                    Missions en cours : {myAcceptedCount} sur {limits?.maxAccepted}
                  </div>
                </div>
                {available.length === 0 ? (
                  <div className="contracts-empty">
                    <p>{filter ? 'Aucun contrat dans cette catégorie.' : 'Aucun contrat disponible pour le moment.'}</p>
                  </div>
                ) : (
                  <ul className="contracts-list">
                    {available.map((c) => renderRow(c, selectedAvailable?.id === c.id, () => setSelectedId(c.id)))}
                  </ul>
                )}
              </section>
              {renderDetail(
                selectedAvailable,
                (c) => (
                  <button
                    type="button"
                    className="contracts-button contracts-button--primary"
                    disabled={myAcceptedCount >= (limits?.maxAccepted ?? 0)}
                    onClick={() => act('accept', c.id)}
                  >
                    Accepter la mission
                  </button>
                ),
                'Choisis un contrat dans la liste pour voir le détail.'
              )}
            </div>
          )}

          {data && tab === 'mine' && (
            mine.length === 0 ? (
              <div className="contracts-empty">
                <p>Tu n'as créé ni accepté de contrat.</p>
                <button type="button" className="contracts-button" onClick={() => goTab('available')}>
                  Voir les contrats disponibles
                </button>
              </div>
            ) : (
              <div className="contracts-split">
                <section className="contracts-pane">
                  <ul className="contracts-list">
                    {mine.map((c) => renderRow(c, selectedMine?.id === c.id, () => setSelectedId(c.id)))}
                  </ul>
                </section>
                {renderDetail(selectedMine, renderMineActions, 'Choisis un contrat dans la liste.')}
              </div>
            )
          )}

          {data && tab === 'create' && limits && (
            <form
              className="contracts-form"
              onSubmit={(e) => { e.preventDefault(); submitCreate(); }}
            >
              <div className="contracts-form__grid">
                <div className="contracts-form__col">
                  <div className="contracts-field">
                    <span className="contracts-field__label">Catégorie</span>
                    <div className="contracts-chips contracts-chips--wrap" role="radiogroup" aria-label="Catégorie">
                      {categories.map((cat) => (
                        <button
                          key={cat.id}
                          type="button"
                          role="radio"
                          aria-checked={form.category === cat.id}
                          className={'contracts-chip' + (form.category === cat.id ? ' contracts-chip--active' : '')}
                          onClick={() => setForm({ ...form, category: cat.id })}
                        >
                          {cat.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <label className="contracts-field">
                    <span className="contracts-field__label">Titre</span>
                    <input
                      className="contracts-input"
                      type="text"
                      value={form.title}
                      maxLength={limits.maxTitle}
                      placeholder="Ce qui doit être fait"
                      onChange={(e) => setForm({ ...form, title: e.target.value })}
                    />
                    <span className="contracts-counter">{form.title.length}/{limits.maxTitle}</span>
                  </label>

                  <label className="contracts-field">
                    <span className="contracts-field__label">Description (optionnelle)</span>
                    <textarea
                      className="contracts-textarea"
                      value={form.description}
                      maxLength={limits.maxDescription}
                      placeholder="Lieu, conditions, détails utiles"
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                    />
                    <span className="contracts-counter">{form.description.length}/{limits.maxDescription}</span>
                  </label>
                </div>

                <div className="contracts-form__col">
                  <label className="contracts-field">
                    <span className="contracts-field__label">Récompense (Septims)</span>
                    <input
                      className="contracts-input"
                      type="number"
                      min={limits.minReward}
                      max={limits.maxReward}
                      step={1}
                      value={form.reward}
                      placeholder={`${limits.minReward} à ${limits.maxReward}`}
                      onChange={(e) => setForm({ ...form, reward: e.target.value })}
                    />
                    {form.reward.trim() !== '' && !rewardValid && (
                      <span className="contracts-error">Entre {limits.minReward} et {limits.maxReward} Septims, en nombre entier.</span>
                    )}
                    {rewardValid && !canAfford && (
                      <span className="contracts-error">Solde insuffisant ({money} Septims).</span>
                    )}
                  </label>

                  <div className="contracts-field">
                    <span className="contracts-field__label">Échéance</span>
                    <div className="contracts-chips contracts-chips--wrap" role="radiogroup" aria-label="Échéance">
                      {data.deadlineDays.map((days) => (
                        <button
                          key={days}
                          type="button"
                          role="radio"
                          aria-checked={form.deadlineDays === days}
                          className={'contracts-chip' + (form.deadlineDays === days ? ' contracts-chip--active' : '')}
                          onClick={() => setForm({ ...form, deadlineDays: days })}
                        >
                          {formatDeadlineChoice(days)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="contracts-summary">
                    <div className="contracts-summary__row">
                      <span>Retenu sur ta bourse</span>
                      <strong>{rewardValid ? `${rewardNumber} Septims` : 'à définir'}</strong>
                    </div>
                    <p className="contracts-hint">
                      La récompense est retenue dès la création et rendue si le contrat est annulé ou expire.
                      Contrats ouverts : {myOpenCount} sur {limits.maxOpen}.
                    </p>
                    <button type="submit" className="contracts-button contracts-button--primary" disabled={!formValid}>
                      Créer le contrat
                    </button>
                  </div>
                </div>
              </div>
            </form>
          )}
        </div>

        <footer className="contracts-close-row">
          <div className="contracts-keyhint">
            <kbd>Échap</kbd> Fermer
          </div>
          <button type="button" className="contracts-button" onClick={close}>
            Fermer
          </button>
        </footer>
      </div>
    </div>
  );
};

export default Contracts;
